import { SetStateAction, Dispatch, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Modal from 'react-modal'
import { HTMLText, Stack } from '@crossfit/ui'
import { Header, Button } from '@crossfit/design-system'
import { useMachine } from '@xstate/react'
import cx from 'classnames'
import style from './style.module.scss'
import ImageCrop from 'components/Admin/DirectoryPage/ImageModal/ImageCrop'
import ImageUpload from 'components/Admin/DirectoryPage/ImageModal/ImageUpload'
import { createAvatarCroppingMachine } from 'components/Admin/DirectoryPage/ImageModal/createAvatarCroppingMachine'
import { uploadAffiliatePhoto } from 'services/affiliates/imageService/upload'
import { GalleryImages } from 'services/affiliates'
import { updateAffiliateAttribute } from 'store/slices/affiliate'
import { getAffiliate } from 'store/selectors'
import { useFormNotification } from 'hooks/useFormNotification'
import {
  updateGalleryPhotos,
  updateLogoPhoto,
  updatePrimaryPhoto,
  updateSchedulePhoto,
} from 'store/slices/DirectoryPage/photos'

export type AffiliateAdminPhotoType =
  | 'primary'
  | 'gallery'
  | 'coach'
  | 'logo'
  | 'schedule'

type Props = {
  type: AffiliateAdminPhotoType
  affiliateId: number
  userId?: number
  isOpen?: boolean
  setIsOpen?: Dispatch<SetStateAction<boolean>>
  setImageUrl?: (string) => void
  setGalleryImages?: Dispatch<SetStateAction<GalleryImages>>
}

export const useCroppingMachine = ({
  type,
  affiliateId,
  userId,
  setIsOpen,
  setImageUrl,
  setGalleryImages,
}: Pick<
  Props,
  | 'type'
  | 'affiliateId'
  | 'userId'
  | 'setIsOpen'
  | 'setImageUrl'
  | 'setGalleryImages'
>) => {
  const affiliateDetail = useSelector(getAffiliate)
  const dispatch = useDispatch()
  const machine = useMemo(() => {
    return createAvatarCroppingMachine()
  }, [])
  const handleFormNotification = useFormNotification()
  return useMachine(machine, {
    services: {
      onSave:
        ({ croppedFile }) =>
        async (send) => {
          try {
            const newAffiliateImages = await uploadAffiliatePhoto(
              type,
              affiliateId,
              croppedFile,
              userId,
              (progressEvent) =>
                send({ type: 'uploadProgress', value: progressEvent }),
            )
            if (newAffiliateImages) {
              if (type === 'coach') {
                setImageUrl(
                  newAffiliateImages.coaches.find(
                    (coach) => coach.id === userId,
                  ).image.url,
                )
              } else if (type === 'primary') {
                dispatch(updatePrimaryPhoto(newAffiliateImages.primary.url))
                setImageUrl(newAffiliateImages.primary.url)
              } else if (type === 'logo') {
                dispatch(updateLogoPhoto(newAffiliateImages.logo.url))
                setImageUrl(newAffiliateImages.logo.url)
              } else if (type === 'gallery') {
                const sortedGallery =
                  newAffiliateImages.gallery && newAffiliateImages.gallery
                    ? newAffiliateImages.gallery
                        .sort((a, b) => a.order - b.order)
                        .map((image) => ({
                          order: image.order,
                          url: image?.url,
                        }))
                    : []

                dispatch(updateGalleryPhotos(sortedGallery))
              } else if (type === 'schedule') {
                dispatch(updateSchedulePhoto(newAffiliateImages.schedule.url))
                setImageUrl(newAffiliateImages.schedule.url)
              }
              handleFormNotification('success')

              setIsOpen(false)
            } else {
              send('serviceError')
            }
          } catch (error) {
            send('serviceError')
          }
        },
    },
  })
}

function ImageModal({
  isOpen,
  type,
  affiliateId,
  userId,
  setIsOpen,
  setImageUrl,
  setGalleryImages,
}: Props) {
  const [state, send] = useCroppingMachine({
    type,
    affiliateId,
    userId,
    setIsOpen,
    setImageUrl,
    setGalleryImages,
  })
  const file = state.context.file
  const handleFile = (file: File) => send({ type: 'fileSelected', value: file })

  const handleCropSubmit = () => {
    send('save')
  }
  const onClose = (e?: any) => {
    e?.preventDefault()
    setIsOpen(false)
  }

  const uploadProgress = state.context.uploadProgress

  useEffect(() => {
    if (uploadProgress === 100) {
      onClose()
    }
  }, [uploadProgress])

  return (
    <Modal
      style={{
        overlay: {
          zIndex: 10,
          backgroundColor: 'rgba(0, 0, 0, .7)',
        },
        content: {
          padding: 0,
          border: 0,
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          inset: 0,
          width: '90%',
          height: 'fit-content',
          maxWidth: 690,
          margin: 'auto',
        },
      }}
      appElement={document.getElementById('app') as HTMLElement}
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={true}
    >
      <div
        className={cx(style.wrapper, {
          [style.upload]:
            state.value === 'idle' || state.value === 'selectingFile',
        })}
      >
        {(state.value === 'idle' || state.value === 'selectingFile') && (
          <>
            <div className={style.header}>
              <Header as='h2' size='md'>
                Edit
              </Header>
            </div>
            <div className={style.uploadWrapper}>
              <ImageUpload onFile={handleFile} />
            </div>
            <div className={style.footer}>
              <Button
                variant='secondary'
                color='white'
                size='sm'
                onClick={onClose}
              >
                {`Cancel`}
              </Button>
              <Button className={style.saveDisabled}>{`Save`}</Button>
            </div>
          </>
        )}
        {(state.value === 'cropping' || state.value === 'saving') && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleCropSubmit()
            }}
            className={style.croppingWrapper}
          >
            <div className={style.header}>
              <Header as='h2' size='md'>
                Upload
              </Header>
            </div>
            <ImageCrop
              file={file}
              onCrop={handleFile}
              aspectRatio={type === 'coach' || type === 'logo' ? 1 : 16 / 9}
              disableGrid={type === 'schedule'}
            />
            {state.value === 'saving' && (
              <div className={style.progressContentWrapper}>
                <Stack
                  direction='horizontal'
                  mb={1}
                  className={style.progressHeader}
                >
                  <HTMLText>{file.name}</HTMLText>
                  <p
                    className={cx(
                      `cf-text medium cf-html-text`,
                      style.progressPercentage,
                      { [style.progressComplete]: uploadProgress === 100 },
                    )}
                  >
                    {uploadProgress}%
                  </p>
                </Stack>
                <div className={style.barContainer}>
                  <span
                    data-progress={uploadProgress}
                    className={style.bar}
                    style={{ width: `${uploadProgress}%` }}
                  ></span>
                </div>
              </div>
            )}
            <div className={style.footer}>
              <Button
                variant='secondary'
                color='white'
                onClick={(e) =>
                  state.value === 'saving' ? e.preventDefault() : onClose(e)
                }
                className={cx(
                  style.cancelButton,
                  state.value === 'saving' && style.disabled,
                )}
              >
                {`Cancel`}
              </Button>
              <Button
                className={cx(
                  style.saveButton,
                  state.value === 'saving' && style.disabled,
                )}
                onClick={() => {
                  if (type === 'schedule') {
                    handleFile(file)
                  }
                }}
              >
                {`Save`}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  )
}

export default ImageModal
